/**
 * Frozen rating display contract (docs/design/add-ratings.md "Foundation additions", ADDENDUM A1).
 * Scale, attribute keys, tiers and formatting live here and only here. Engine constants
 * (priors, curves, weights) stay in shared/src/domain/stats; a retune is an ENGINE_VERSION bump.
 */

export const RATING_MIN = 0;
export const RATING_MAX = 99;
export const ELO_START = 1500;

export const ATTR_KEYS = ['3PT', 'MID', 'FIN', 'FT', 'PLAYMAKING', 'DEF', 'REB', 'HUSTLE', 'CLU'] as const;
export type AttrKey = (typeof ATTR_KEYS)[number];
/** Alias kept for readers who look for the long name. Same array. */
export const ATTRIBUTE_KEYS = ATTR_KEYS;

export const ATTR_ABBR: Record<AttrKey, string> = {
  '3PT': '3PT',
  MID: 'MID',
  FIN: 'FIN',
  FT: 'FT',
  PLAYMAKING: 'PLY',
  DEF: 'DEF',
  REB: 'REB',
  HUSTLE: 'HUS',
  CLU: 'CLU',
};

export const ATTR_LABEL: Record<AttrKey, string> = {
  '3PT': 'Three point',
  MID: 'Midrange',
  FIN: 'Finishing',
  FT: 'Free throws',
  PLAYMAKING: 'Playmaking',
  DEF: 'Defense',
  REB: 'Rebounding',
  HUSTLE: 'Hustle',
  CLU: 'Clutch',
};

/** The fixed six on the large card (R19). The small card shows the top 3. */
export const CARD_L_ATTRS = ['3PT', 'MID', 'FIN', 'FT', 'DEF', 'CLU'] as const satisfies readonly AttrKey[];

export const RATING_STATES = ['unrated', 'provisional', 'rated', 'rusty'] as const;
export type RatingState = (typeof RATING_STATES)[number];

export const PROVISIONAL_CAP = { ovr: 79, attr: 89 } as const;

export const TIERS = [
  { id: 'benchwarmer', name: 'Benchwarmer', min: 0, max: 44, shape: 'circle', fill: '#ffc299' },
  { id: 'rotation', name: 'Rotation', min: 45, max: 56, shape: 'square', fill: '#ffffff' },
  { id: 'starter', name: 'Starter', min: 57, max: 67, shape: 'shield', fill: '#ffffa1' },
  { id: 'allcourt', name: 'All-Court', min: 68, max: 79, shape: 'hexagon', fill: '#e2a4ff' },
  { id: 'franchise', name: 'Franchise', min: 80, max: 89, shape: 'diamond', fill: '#97f0ff' },
  {
    id: 'myth',
    name: 'Myth',
    min: 90,
    max: 99,
    shape: 'star8',
    fill: '#e2a4ff',
    stack: ['#97f0ff', '#92ffa7', '#ffffa1', '#ffc299'],
  },
] as const;

export type Tier = (typeof TIERS)[number];
export type TierId = Tier['id'];
export type TierShape = Tier['shape'];
export const TIER_IDS = TIERS.map((t) => t.id) as [TierId, ...TierId[]];

export const TIER_SHADOW = '#813f98';
export const TIER_OUTLINE = '#000000';
export const UNRATED_FILL = '#edd0e4';

/** Round and clamp any number into 0..99. NaN becomes 0. */
export function clampRating(v: number): number {
  if (!Number.isFinite(v)) return v === Number.POSITIVE_INFINITY ? RATING_MAX : RATING_MIN;
  return Math.min(RATING_MAX, Math.max(RATING_MIN, Math.round(v)));
}

/** Tier for an OVR. Clamps to 0..99 first, so 104 is Myth and -3 is Benchwarmer. */
export function tierFor(ovr: number): TierId {
  const v = clampRating(ovr);
  for (const t of TIERS) if (v >= t.min && v <= t.max) return t.id;
  return 'benchwarmer';
}

export function tierById(id: TierId): Tier {
  const t = TIERS.find((x) => x.id === id);
  if (!t) throw new Error(`Unknown tier ${id}`);
  return t;
}

/** Card text for a rating: '??' for null, else the clamped integer with no leading zero. */
export function formatRating(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '??';
  return String(clampRating(v));
}
